import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { Card } from './card';
import { useTheme } from 'next-themes';

export default function WorldMapECharts({ data, filterType }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [mapLoaded, setMapLoaded] = useState(false);
  const [chartKey, setChartKey] = useState(0); // Force re-render when filter changes

  useEffect(() => {
    // We must load a world map GeoJSON for ECharts to know how to draw the world
    // Note: ECharts 5+ removed bundled maps, so we fetch from the 4.9.0 archive
    fetch('https://cdn.jsdelivr.net/npm/echarts@4.9.0/map/json/world.json')
      .then((response) => response.json())
      .then((worldJson) => {
        echarts.registerMap('world', worldJson);
        setMapLoaded(true);
      })
      .catch((error) => console.error("Error loading world map data", error));
  }, []);

  // Force re-render when filterType changes
  useEffect(() => {
    setChartKey(prev => prev + 1);
  }, [filterType, data]);

  if (!mapLoaded) {
    return (
      <Card className="bg-white rounded-2xl shadow-md p-6 border border-slate-100 h-[400px] flex items-center justify-center">
        <p className="text-slate-500">Loading Map Geometry...</p>
      </Card>
    );
  }

  // Transform data format depending on what filter is active
  // By default ECharts world map expects 'name' to be the country name like 'United States'
  let mapData = [];
  let showMap = true;
  let mapTitle = 'Global Traffic Map';
  let mapSubtitle = 'Visitor distribution across the world';

  if (data && data.length > 0) {
    if (filterType === 'countries') {
      // Show countries on the world map
      mapData = data.map(item => ({
        name: item.country_name || 'Unknown',
        value: item.visitors || 0
      }));
      mapSubtitle = 'Visitor distribution by country';
    } else if (filterType === 'continents') {
      // For continents, show countries colored by their continent
      // Since ECharts world map doesn't support continent aggregation well,
      // we'll show all countries but highlight based on continent data
      showMap = false;
      mapTitle = 'Continent View';
      mapSubtitle = 'Continent data is shown in the list below. Switch to Countries to see the map.';
    } else if (filterType === 'regions') {
      // Regions are too granular for world map - aggregate by country
      const groupedByCountry = {};
      data.forEach(item => {
        const cName = item.country_name || item.country || 'Unknown';
        groupedByCountry[cName] = (groupedByCountry[cName] || 0) + (item.visitors || 0);
      });
      mapData = Object.keys(groupedByCountry).map(name => ({
        name,
        value: groupedByCountry[name]
      }));
      mapSubtitle = 'Visitor distribution by region (aggregated by country)';
    } else if (filterType === 'cities') {
      // Cities are too granular for world map - aggregate by country
      const groupedByCountry = {};
      data.forEach(item => {
        const cName = item.country_name || item.country || 'Unknown';
        groupedByCountry[cName] = (groupedByCountry[cName] || 0) + (item.visitors || 0);
      });
      mapData = Object.keys(groupedByCountry).map(name => ({
        name,
        value: groupedByCountry[name]
      }));
      mapSubtitle = 'Visitor distribution by city (aggregated by country)';
    }
  }

  console.log('WorldMapECharts - filterType:', filterType, 'data length:', data?.length, 'mapData length:', mapData.length, 'showMap:', showMap);

  const maxVisitors = mapData.length > 0 ? Math.max(...mapData.map(d => d.value)) : 100;

  // Adjust colors based on theme
  const mapColorRange = isDark
    ? ['#1c2e26', '#254236', '#2d5746', '#356b56', '#FEAB3D'] // Dark theme green range to accent
    : ['#e0f2f1', '#a5d6a7', '#4caf50', '#2e7d32', '#1b5e20']; // Light theme green range

  const mapBorderColor = isDark ? '#334155' : '#ffffff'; // slate-700 or white
  const mapAreaColor = isDark ? '#1e293b' : '#f8fafc'; // slate-800 or slate-50

  const option = {
    tooltip: {
      trigger: 'item',
      showDelay: 0,
      transitionDuration: 0.2,
      backgroundColor: isDark ? '#1e293b' : '#ffffff',
      borderColor: isDark ? '#334155' : '#e2e8f0',
      textStyle: {
        color: isDark ? '#f8fafc' : '#0f172a'
      },
      formatter: function (params) {
        if (!params.value && params.value !== 0) return params.name;
        const value = (params.value + '').split('.');
        const valueStr = value[0].replace(/(\d{1,3})(?=(?:\d{3})+(?!\d))/g, '$1,');
        return params.name + ': ' + (isNaN(params.value) ? 0 : valueStr) + ' visitors';
      }
    },
    visualMap: {
      left: 'right',
      min: 0,
      max: maxVisitors,
      inRange: {
        color: mapColorRange
      },
      text: ['High', 'Low'],
      textStyle: {
        color: isDark ? '#94a3b8' : '#64748b' // slate-400 or slate-500
      },
      calculable: false,
      show: false
    },
    series: [
      {
        name: 'World Traffic',
        type: 'map',
        map: 'world',
        roam: true,
        emphasis: {
          label: {
            show: true,
            color: isDark ? '#ffffff' : '#000000'
          },
          itemStyle: {
            areaColor: isDark ? '#4ade80' : '#81c784' // Lighter green on hover
          }
        },
        data: mapData,
        itemStyle: {
          areaColor: mapAreaColor,
          borderColor: mapBorderColor,
          borderWidth: 0.5
        }
      }
    ]
  };

  return (
    <Card className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none p-4 border border-slate-100 dark:border-border flex flex-col relative w-full overflow-hidden h-full min-h-[450px] transition-colors">
      <div className="absolute top-6 left-6 z-10 pointer-events-none">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white drop-shadow-sm">{mapTitle}</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 drop-shadow-sm">{mapSubtitle}</p>
      </div>

      {!showMap ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">🌍</div>
            <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Continent View</h4>
            <p className="text-slate-600 dark:text-slate-400 mb-4">Continent-level data is displayed in the list below.</p>
            <p className="text-sm text-slate-500 dark:text-slate-500">Switch to "Countries" to see the world map visualization.</p>
          </div>
        </div>
      ) : (
        <ReactECharts
          key={chartKey} // Force re-render when filter changes
          option={option}
          style={{ height: '100%', width: '100%', minHeight: '380px', marginTop: '20px' }}
          className="react_for_echarts"
          opts={{ renderer: 'canvas' }}
          onChartReady={(chart) => {
            // Optimize canvas for frequent read operations to fix performance warning
            const canvas = chart.getDom().querySelector('canvas');
            if (canvas && !canvas.hasAttribute('willReadFrequently')) {
              canvas.setAttribute('willReadFrequently', 'true');
              console.log('Canvas willReadFrequently attribute set for ECharts');
            }
          }}
        />
      )}
    </Card>
  );
}
